import { Controller } from "@hotwired/stimulus"

// Walks a redrawn algorithm one decision at a time. Steps run on by themselves; each
// decision waits for the student's answer, in the order the walk reaches them. An earlier
// answer can be changed, and everything after it is redrawn. A step several branches
// lead to ("¿continúa la hemorragia?") appears once, and an answer that leads back to a
// step already shown says so rather than ending the walk. The boxes are the server's,
// kept in <template>s.
//
// The node and the answer are read from the button's data attributes, not Stimulus
// params: params go through JSON.parse, which turns an answer such as "1" into a number.
export default class extends Controller {
  static targets = ["template", "trail", "end", "back", "restart", "status"]
  static values = { start: String, shapes: Object, edges: Array, loop: String, announce: String }

  connect() {
    this.answers = []
    this.render()
  }

  choose(event) {
    const { figureWalkNode: node, choice: label } = event.currentTarget.dataset
    const asked = this.asked.indexOf(node)
    if (asked === -1 || this.answers[asked] === label) return

    this.answers = [...this.answers.slice(0, asked), label]
    this.render()
    this.focusAnswer(node, label)
  }

  back() {
    this.answers = this.answers.slice(0, -1)
    this.render()
  }

  restart() {
    this.answers = []
    this.render()
  }

  render() {
    const before = this.trailTarget.children.length
    this.trailTarget.replaceChildren()
    this.asked = []
    const seen = new Set()
    const followed = new Map()
    const waiting = []
    let frontier = [this.startValue]
    let carried = []
    let finished = false
    let looped = false

    const follow = (id, targets) => {
      followed.set(id, targets)
      targets.filter((target) => seen.has(target) && this.returnsTo(target, id, followed)).forEach((target) => {
        this.showLoop(target)
        looped = true
      })
      return targets
    }

    for (;;) {
      const shown = frontier.filter((id) => !seen.has(id))
      shown.forEach((id) => { seen.add(id); this.show(id) })
      waiting.push(...shown.filter((id) => this.isDecision(id)))
      carried = this.unique([...carried, ...shown.filter((id) => !this.isDecision(id)).flatMap((id) => follow(id, this.targetsOf(id)))])
        .filter((id) => !seen.has(id))

      if (waiting.length) {
        const decision = waiting.shift()
        this.asked.push(decision)
        const answer = this.answers[this.asked.length - 1]
        if (answer === undefined) break

        this.mark(decision, answer)
        const chosen = follow(decision, this.targetsOf(decision, answer))
        frontier = this.unique([...chosen, ...carried])
        carried = []
      } else if (carried.length) {
        frontier = carried
        carried = []
      } else {
        finished = !looped
        break
      }
    }

    this.answers = this.answers.slice(0, this.asked.length)
    this.endTarget.hidden = !finished
    this.backTarget.disabled = this.answers.length === 0
    this.restartTarget.disabled = this.answers.length === 0
    const added = this.trailTarget.children.length - before
    this.statusTarget.textContent = added > 0 ? this.announceValue.replace("%{count}", added) : ""
  }

  show(id) {
    this.trailTarget.append(this.templateFor(id).content.cloneNode(true))
  }

  showLoop(id) {
    const item = document.createElement("li")
    item.className = "py-2 text-center text-sm text-ink-muted"
    item.textContent = this.loopValue.replace("%{step}", this.templateFor(id).dataset.text)
    this.trailTarget.append(item)
  }

  mark(decision, answer) {
    this.buttonsOf(decision).forEach((button) => {
      button.setAttribute("aria-pressed", String(button.dataset.choice === answer))
    })
  }

  focusAnswer(node, label) {
    this.buttonsOf(node).find((button) => button.dataset.choice === label)?.focus({ preventScroll: true })
  }

  buttonsOf(node) {
    return [...this.trailTarget.querySelectorAll("button[data-figure-walk-node]")]
      .filter((button) => button.dataset.figureWalkNode === node)
  }

  templateFor(id) {
    return this.templateTargets.find((element) => element.dataset.id === id)
  }

  isDecision(id) {
    return this.shapesValue[id] === "decision"
  }

  targetsOf(id, label) {
    return this.edgesValue
      .filter((edge) => edge.from === id && (label === undefined || edge.label === label))
      .map((edge) => edge.to)
  }

  unique(ids) {
    return [...new Set(ids)]
  }

  // Only arrows actually followed can establish a return; a shared step is not a loop.
  returnsTo(start, goal, followed) {
    const visited = new Set()
    const queue = [start]
    while (queue.length) {
      const id = queue.shift()
      if (id === goal) return true
      if (visited.has(id)) continue
      visited.add(id)
      queue.push(...(followed.get(id) || []))
    }
    return false
  }
}
